"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useMediaQuery } from "@mui/material"
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material"
import {
  FilterList as FilterListIcon,
  Restaurant as RestaurantIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import apiClient from "../utils/apiClient"
import { format } from "date-fns"
import FilterDialog from "../components/FilterDialog"
import moment from "moment-timezone"
import { motion, AnimatePresence } from "framer-motion"
import SkeletonLoader from "../components/SkeletonLoader"
import { useSlots } from "../contexts/SlotsContext"

const API_BASE_URL = process.env.REACT_APP_API_URL
const KOLKATA_TIMEZONE = "Asia/Kolkata"

// Helper function to get period icon
const getPeriodIcon = (period) => {
  if (!period) return "🕐"
  if (period.toLowerCase().includes("breakfast")) return "🍳"
  if (period.toLowerCase().includes("lunch")) return "🍽️"
  if (period.toLowerCase().includes("dinner")) return "🍛"
  if (period.toLowerCase().includes("evening snacks")) return "🍿"
  return "🕐"
}

// Helper function to get period background color
const getPeriodBackground = (period) => {
  if (!period) return "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
  if (period.toLowerCase().includes("breakfast")) return "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
  if (period.toLowerCase().includes("lunch")) return "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
  if (period.toLowerCase().includes("dinner")) return "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
  if (period.toLowerCase().includes("evening snacks")) return "linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)"
  if (period === "morning") return "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
  if (period === "evening") return "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
  return "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)"
}

// Helper function to get meal type icon
const getMealTypeIcon = (mealType) => {
  if (!mealType) return ""
  if (mealType.toLowerCase() === "veg") return ""
  if (mealType.toLowerCase() === "non-veg") return ""
  return ""
}

// Helper function to get meal type color
const getMealTypeColor = (mealType) => {
  if (!mealType) return "#757575"
  if (mealType.toLowerCase() === "veg") return "#2e7d32"
  if (mealType.toLowerCase() === "non-veg") return "#ed6c02"
  return "#673ab7" // Purple for custom meal types
}

const AllRequestList = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [requests, setRequests] = useState([])
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    requestId: null,
  })
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterStartDate, setFilterStartDate] = useState("")
  const [filterEndDate, setFilterEndDate] = useState("")
  const [filterEmail, setFilterEmail] = useState("")
  const [filterDepartment, setFilterDepartment] = useState("")
  const [filterSortOrder, setFilterSortOrder] = useState("desc")
  const [orderIdSearch, setOrderIdSearch] = useState("")
  const [limit] = useState(10)

  // Updated pagination state for nextKey approach
  const [nextKey, setNextKey] = useState(null)
  const [previousKeys, setPreviousKeys] = useState([]) // Stack to track previous page keys
  const [currentPageNumber, setCurrentPageNumber] = useState(1) // For display purposes
  const [hasNextPage, setHasNextPage] = useState(false)

  const [loading, setLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false)
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")
  const [rejectionReasonError, setRejectionReasonError] = useState(false)
  const { slotsData } = useSlots()

  const handleFilterOpen = () => {
    setFilterOpen(true)
  }

  const handleFilterClose = () => {
    setFilterOpen(false)
  }

  // Updated fetchRequests to work with nextKey pagination
  const fetchRequests = useCallback(
    async (requestNextKey = null, filters = {}, isNewSearch = false, isSearch = false) => {
      if (isSearch) {
        setSearchLoading(true)
      } else {
        setLoading(true)
      }
      try {

        let startDateEpoch = ""
        let endDateEpoch = ""
        let department = ""
        let email = ""
        let orderIdSearchParam = ""

        // Use passed filters or current state
        const currentFilters = {
          startDate: filters.startDate !== undefined ? filters.startDate : filterStartDate,
          endDate: filters.endDate !== undefined ? filters.endDate : filterEndDate,
          department: filters.department !== undefined ? filters.department : filterDepartment,
          email: filters.email !== undefined ? filters.email : filterEmail,
          sortOrder: filters.sortOrder !== undefined ? filters.sortOrder : filterSortOrder,
          orderIdSearch: filters.orderIdSearch !== undefined ? filters.orderIdSearch : orderIdSearch,
        }

        if (currentFilters.startDate) {
          const startDateObj = new Date(currentFilters.startDate)
          startDateObj.setHours(0, 0, 0, 0)
          startDateEpoch = Math.floor(startDateObj.getTime() / 1000).toString()
        }

        if (currentFilters.endDate) {
          const endDateObj = new Date(currentFilters.endDate)
          endDateObj.setHours(23, 59, 59, 999)
          endDateEpoch = Math.floor(endDateObj.getTime() / 1000).toString()
        }

        if (currentFilters.department) {
          department = currentFilters.department
        }
        if (currentFilters.email) {
          email = currentFilters.email
        }
        if (currentFilters.orderIdSearch) {
          orderIdSearchParam = currentFilters.orderIdSearch
        }

        const params = new URLSearchParams({
          startDate: startDateEpoch,
          endDate: endDateEpoch,
          sortOrder: currentFilters.sortOrder,
          limit: limit.toString(),
          email: email,
          department: department,
          orderIdSearch: orderIdSearchParam,
        })

        // Add nextKey to params if provided
        if (requestNextKey) {
          params.append("nextKey", requestNextKey)
        }

        const response = await apiClient.get(`/admin/requests?${params.toString()}`)

        setRequests(response.data.items)

        // Update pagination state based on new API structure
        const newNextKey = response.data.pagination?.nextKey || null
        setNextKey(newNextKey)
        setHasNextPage(!!newNextKey)

        // Reset pagination tracking for new searches
        if (isNewSearch) {
          setPreviousKeys([])
          setCurrentPageNumber(1)
        }
      } catch (error) {
        console.error("Error fetching requests:", error)



        toast.error("Failed to fetch requests")
      } finally {
        if (isSearch) {
          setSearchLoading(false)
        } else {
          setLoading(false)
        }
      }
    },
    [limit],
  )

  // Full order ID search - only search when complete order ID is entered
  useEffect(() => {
    // Skip if this is the initial load (orderIdSearch is empty and we haven't loaded yet)
    if (orderIdSearch === "" && loading) {
      return
    }
    
    const timeoutId = setTimeout(() => {
      if (orderIdSearch.trim() && orderIdSearch.trim().length >= 16) {
        // Only search if order ID looks complete (at least 16 characters)
        fetchRequests(null, { orderIdSearch }, true, true)
      } else if (orderIdSearch.trim() === "") {
        // Clear search - fetch all requests
        fetchRequests(null, {}, true, true)
      }
      // If partial order ID, don't search
    }, 1500)
    
    return () => clearTimeout(timeoutId)
  }, [orderIdSearch])

  // Initial load - only runs once
  useEffect(() => {
    fetchRequests(null, {}, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/requests/delete?requestKey=${encodeURIComponent(deleteDialog.requestId)}`)

      toast.success("Request deleted successfully")
      // Refresh current page after delete
      fetchRequests(previousKeys[previousKeys.length - 1] || null, {
        startDate: filterStartDate,
        endDate: filterEndDate,
        department: filterDepartment,
        email: filterEmail,
        sortOrder: filterSortOrder,
        orderIdSearch: orderIdSearch,
      })
    } catch (error) {
      console.error("Error deleting request:", error)
      toast.error("Failed to delete request")
    } finally {
      setDeleteDialog({ open: false, requestId: null })
    }
  }

  // Apply filters function - this is called only when Apply button is clicked
  const applyFilters = () => {
    fetchRequests(
      null,
      {
        startDate: filterStartDate,
        endDate: filterEndDate,
        department: filterDepartment,
        email: filterEmail,
        sortOrder: filterSortOrder,
        orderIdSearch: orderIdSearch,
      },
      true,
    )
    handleFilterClose()
  }

  const clearFilters = () => {
    setFilterStartDate("")
    setFilterEndDate("")
    setFilterDepartment("")
    setFilterEmail("")
    setFilterSortOrder("desc")
    setOrderIdSearch("")
    // Clear filters and reset pagination
    fetchRequests(
      null,
      {
        startDate: "",
        endDate: "",
        department: "",
        email: "",
        sortOrder: "desc",
        orderIdSearch: "",
      },
      true,
    )
  }

  const handleExportCsv = async () => {
    // Check if there are any records to export
    if (requests.length === 0) {
      toast.error("No records available to export")
      return
    }

    setExportLoading(true)
    try {
      let startDateEpoch = ""
      let endDateEpoch = ""
      let department = ""
      let email = ""

      // Use current filter values
      if (filterStartDate) {
        const startDateObj = new Date(filterStartDate)
        startDateObj.setHours(0, 0, 0, 0)
        startDateEpoch = Math.floor(startDateObj.getTime() / 1000).toString()
      }

      if (filterEndDate) {
        const endDateObj = new Date(filterEndDate)
        endDateObj.setHours(23, 59, 59, 999)
        endDateEpoch = Math.floor(endDateObj.getTime() / 1000).toString()
      }

      if (filterDepartment) {
        department = filterDepartment
      }
      if (filterEmail) {
        email = filterEmail
      }

      const params = new URLSearchParams({
        startDate: startDateEpoch,
        endDate: endDateEpoch,
        sortOrder: filterSortOrder,
        email: email,
        department: department,
        orderIdSearch: orderIdSearch,
        exportToCsv: "true",
      })

      const response = await apiClient.get(`/admin/requests?${params.toString()}`)

      // Check if response contains fileUrl
      if (response.data.fileUrl) {
        // Create a temporary link to download the file
        const link = document.createElement("a")
        link.href = response.data.fileUrl
        link.download = `food-requests-export-${new Date().toISOString().split("T")[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        toast.success("CSV file downloaded successfully!")
      } else {
        toast.error("Failed to generate CSV file")
      }
    } catch (error) {
      console.error("Error exporting CSV:", error)



      toast.error("Failed to export CSV file")
    } finally {
      setExportLoading(false)
    }
  }

  // Updated pagination handlers for nextKey approach
  const handleNextPage = () => {
    if (hasNextPage && nextKey) {
      // Store current position in previous keys stack
      const currentKey = previousKeys[previousKeys.length - 1] || null
      setPreviousKeys((prev) => [...prev, currentKey])
      setCurrentPageNumber((prev) => prev + 1)

      fetchRequests(nextKey, {
        startDate: filterStartDate,
        endDate: filterEndDate,
        department: filterDepartment,
        email: filterEmail,
        sortOrder: filterSortOrder,
        orderIdSearch: orderIdSearch,
      })
    }
  }

  const handlePreviousPage = () => {
    if (previousKeys.length > 0) {
      // Get the previous key from stack
      const newPreviousKeys = [...previousKeys]
      const previousKey = newPreviousKeys.pop()
      setPreviousKeys(newPreviousKeys)
      setCurrentPageNumber((prev) => prev - 1)

      fetchRequests(previousKey, {
        startDate: filterStartDate,
        endDate: filterEndDate,
        department: filterDepartment,
        email: filterEmail,
        sortOrder: filterSortOrder,
        orderIdSearch: orderIdSearch,
      })
    }
  }

  const handleMenuOpen = (event, request) => {
    setAnchorEl(event.currentTarget)
    setSelectedRequest(request)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRequest(null)
  }

  const handleApproveClick = () => {
    setApproveConfirmOpen(true)
  }

  const handleRejectClick = () => {
    setRejectConfirmOpen(true)
  }

  const handleApproveConfirmClose = () => {
    setApproveConfirmOpen(false)
    setSelectedRequest(null)
    handleMenuClose()
  }

  const handleRejectConfirmClose = () => {
    setRejectConfirmOpen(false)
    setSelectedRequest(null)
    setRejectionReason("")
    setRejectionReasonError(false)
    handleMenuClose()
  }

  const handleUpdateStatus = async (status, reason = null) => {
    if (!selectedRequest) return

    if (status === "REJECTED") {
      if (!reason) {
        setRejectionReasonError(true)
        return
      }
      setRejectionReasonError(false)
    }

    try {
      const updatePayload = {
        status: status,
        rejectionReason: reason,
      }

      const requestId = selectedRequest.requestKey
      const encodedRequestId = encodeURIComponent(requestId)

      await apiClient.patch(`/requests/${encodedRequestId}`, updatePayload)

      toast.success(`Request ${status.toLowerCase()}ed successfully`)
      // Refresh current page after status update
      fetchRequests(previousKeys[previousKeys.length - 1] || null, {
        startDate: filterStartDate,
        endDate: filterEndDate,
        department: filterDepartment,
        email: filterEmail,
        sortOrder: filterSortOrder,
        orderIdSearch: orderIdSearch,
      })
    } catch (error) {
      console.error(`Error updating request status to ${status}:`, error)
      toast.error(`Failed to ${status.toLowerCase()} request`)
    } finally {
      handleApproveConfirmClose()
      handleRejectConfirmClose()
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <SkeletonLoader type="table" rows={8} />
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: { xs: 10, sm: 4 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Enhanced Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: 4,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: 4,
              color: "white",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background: "conic-gradient(from 180deg, transparent, rgba(255,255,255,0.08), transparent)",
                animation: "rotate 25s linear infinite",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <motion.div
                    animate={{
                      rotate: [0, 15, -15, 0],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  >
                    <RestaurantIcon sx={{ fontSize: 48 }} />
                  </motion.div>
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        background: "linear-gradient(45deg, #fff 30%, #ffe0b2 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                      }}
                    >
                      All Food Requests
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        opacity: 0.9,
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Manage and review all food requests
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
                  <motion.div
                    style={{
                      background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)",
                      borderRadius: "16px",
                      backdropFilter: "blur(15px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleExportCsv}
                      disabled={exportLoading || requests.length === 0}
                      sx={{
                        bgcolor: "transparent",
                        color: "white",
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: "16px",
                        px: 4,
                        py: 1.5,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "transparent",
                          boxShadow: "0 8px 32px rgba(255,255,255,0.25)",
                          transform: "none",
                        },
                        "&:disabled": {
                          bgcolor: "rgba(255,255,255,0.1)",
                          color: "rgba(255,255,255,0.5)",
                        },
                      }}
                    >
                      {exportLoading ? "Exporting..." : isMobile ? "📊 Export" : "📊 Export CSV"}
                    </Button>
                  </motion.div>
                  <motion.div
                    style={{
                      background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)",
                      borderRadius: "16px",
                      backdropFilter: "blur(15px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleFilterOpen}
                      startIcon={<FilterListIcon />}
                      sx={{
                        bgcolor: "transparent",
                        color: "white",
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: "16px",
                        px: 4,
                        py: 1.5,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "transparent",
                          boxShadow: "0 8px 32px rgba(255,255,255,0.25)",
                          transform: "none",
                        },
                      }}
                    >
                      {isMobile ? "🔍" : "🔍 Filter"}
                    </Button>
                  </motion.div>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Order ID Search */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Enter complete Order ID (e.g., ORD1735123456)"
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              helperText={
                orderIdSearch.trim() && orderIdSearch.trim().length < 16
                  ? "Please enter the complete Order ID (minimum 16 characters)"
                  : "Enter the full Order ID to search"
              }
              error={orderIdSearch.trim() && orderIdSearch.trim().length < 16}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover fieldset': {
                    borderColor: '#20d5d5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#20d5d5',
                  },
                },
                '& .MuiFormHelperText-root': {
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </motion.div>

        {/* Enhanced Table */}
        <motion.div variants={itemVariants}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 4,
              overflow: "auto",
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 25px 80px rgba(0,0,0,0.12)",
              WebkitOverflowScrolling: "touch",
              '&::-webkit-scrollbar': {
                height: { xs: 4, sm: 6 },
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(32, 213, 213, 0.5)',
                borderRadius: 3,
                '&:hover': {
                  background: 'rgba(32, 213, 213, 0.7)',
                },
              },
            }}
          >
            <Table sx={{ 
              minWidth: 1000, 
              tableLayout: 'auto', 
              width: '100%',
              '& .MuiTableContainer-root::-webkit-scrollbar': {
                height: { xs: 4, sm: 6 },
              },
            }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 50%, #0891b2 100%)",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                    },
                  }}
                >
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '13%' }}>
                    Order ID
                  </TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '8%' }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '9%' }}>
                    Submitted
                  </TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '9%' }}>Name</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '11%' }}>Email</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '7%' }}>Department</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '7%' }}>Type</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '7%' }}>Meal</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '8%' }}>Status</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '16%' }}>Reason</TableCell>
                  <TableCell sx={{ py: 3, color: "white", fontWeight: 700, fontSize: "16px", width: '7%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {searchLoading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          🔍 Searching...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 12 }}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5 }}Food requested for
                        >
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                              borderRadius: 4,
                              p: 8,
                              border: "3px dashed #cbd5e1",
                            }}
                          >
                            <motion.div
                              animate={{
                                y: [0, -15, 0],
                                rotate: [0, 5, -5, 0],
                              }}
                              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY }}
                            >
                              <Typography variant="h1" sx={{ mb: 3 }}>
                                🔍
                              </Typography>
                            </motion.div>
                            <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
                              No requests found
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                              Try adjusting your filters or check back later
                            </Typography>
                          </Box>
                        </motion.div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((request, index) => (
                      <motion.tr
                        key={request.requestKey || `${request.id}-${request.createdAt}`}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ delay: index * 0.05 }}
                        component={TableRow}
                        sx={{
                          position: "relative",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, rgba(32, 213, 213, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)",
                            transform: "scale(1.01)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "3%",
                            right: "3%",
                            height: "1px",
                            background: "linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 3 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              background: "linear-gradient(45deg, #ff9800 30%, #f57c00 90%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {request.orderId || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{
                              background: "linear-gradient(45deg, #20d5d5 30%, #16B1C8 90%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {request.requestDate
                              ? format(
                                  moment.tz(request.requestDate, "DD/MM/YYYY", KOLKATA_TIMEZONE).toDate(),
                                  "MMM dd, yyyy",
                                )
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {format(new Date(request.createdAt * 1000), "MMM dd, yyyy HH:mm")}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Tooltip title={request.displayName.length > 20 ? request.displayName : ""} arrow placement="top">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                background: "linear-gradient(45deg, #2c3e50 30%, #34495e 90%)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "140px",
                                cursor: request.displayName.length > 20 ? "pointer" : "default",
                              }}
                            >
                              {request.displayName}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Tooltip title={request.email.length > 25 ? request.email : ""} arrow placement="top">
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "180px",
                                cursor: request.email.length > 25 ? "pointer" : "default",
                                "&:hover": {
                                  color: request.email.length > 25 ? "#20d5d5" : "inherit",
                                },
                              }}
                            >
                              {request.email}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Tooltip title={(request.departmentName || "N/A").length > 15 ? (request.departmentName || "N/A") : ""} arrow placement="top">
                            <Typography 
                              variant="body2" 
                              fontWeight={600}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "120px",
                                cursor: (request.departmentName || "N/A").length > 15 ? "pointer" : "default",
                                "&:hover": {
                                  color: (request.departmentName || "N/A").length > 15 ? "#20d5d5" : "inherit",
                                },
                              }}
                            >
                              {request.departmentName || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Chip
                            label={request.period || request.requestType || "Unknown"}
                            sx={{
                              background: getPeriodBackground(request.period || request.requestType),
                              fontWeight: 700,
                              borderRadius: 3,
                              border: "1px solid rgba(255,255,255,0.4)",
                              backdropFilter: "blur(10px)",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Tooltip title={(request.mealType || "Unknown").length > 15 ? (request.mealType || "Unknown") : ""} arrow placement="top">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                color: getMealTypeColor(request.mealType),
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "120px",
                                cursor: (request.mealType || "Unknown").length > 15 ? "pointer" : "default",
                                "&:hover": {
                                  opacity: (request.mealType || "Unknown").length > 15 ? 0.8 : 1,
                                },
                              }}
                            >
                              {getMealTypeIcon(request.mealType)} {request.mealType || "Unknown"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontSize: "0.9rem",
                              background: "linear-gradient(45deg, #2c3e50 30%, #34495e 90%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              textTransform: "none",
                            }}
                          >
                            {request.status.charAt(0) + request.status.slice(1).toLowerCase()}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          {request.reason ? (
                            <Tooltip title={request.reason} arrow placement="top" sx={{ maxWidth: 400 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  "&:hover": {
                                    color: "#20d5d5",
                                    fontWeight: 600,
                                  },
                                }}
                              >
                                {request.reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                              No reason
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 3 }}>
                          {(() => {
                            const requestDateMoment = moment.tz(request.requestDate, 'DD/MM/YYYY', KOLKATA_TIMEZONE);
                            const todayMoment = moment().tz(KOLKATA_TIMEZONE);
                            const isToday = requestDateMoment.isSame(todayMoment, 'day');
                            
                            return isToday ? (
                              <Button
                                size="small"
                                onClick={() => {
                                  // Construct correct requestKey from available data
                                  const formattedDate = moment.tz(request.requestDate, 'DD/MM/YYYY', KOLKATA_TIMEZONE).format('YYYY-MM-DD');
                                  const correctRequestKey = `${request.email}#${formattedDate}#${request.period}`;
                                  console.log('Constructed requestKey:', correctRequestKey);
                                  setDeleteDialog({ open: true, requestId: correctRequestKey });
                                }}
                                sx={{
                                  minWidth: 'auto',
                                  p: 1,
                                  borderRadius: 2,
                                  color: '#ef4444',
                                  '&:hover': {
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    color: '#dc2626'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            ) : null;
                          })()}
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </motion.div>

        {/* Enhanced Pagination with nextKey approach */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              p: 3,
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
              backdropFilter: "blur(20px)",
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.3)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.1)",
            }}
          >
            <motion.div>
              <Button
                onClick={handlePreviousPage}
                disabled={previousKeys.length === 0 || loading}
                variant="contained"
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  px: 4,
                  background: "linear-gradient(135deg, rgb(20, 28, 38) 0%, rgb(18, 32, 48) 100%)",
                  boxShadow: "0 8px 25px rgba(30, 30, 60, 0.4)",
                  "&:disabled": {
                    background: "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)",
                    boxShadow: "none",
                  },
                  "&:hover": {
                    background: "linear-gradient(135deg, rgb(20, 28, 38) 0%, rgb(18, 32, 48) 100%)",
                    boxShadow: "0 8px 25px rgba(30, 30, 60, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                ← Previous
              </Button>
            </motion.div>
            <Box
              sx={{
                background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                borderRadius: 3,
                px: 4,
                py: 1.5,
                color: "white",
                boxShadow: "0 8px 25px rgba(32, 213, 213, 0.3)",
              }}
            >
              <Typography variant="h6" fontWeight={800}>
                Page {currentPageNumber}
              </Typography>
            </Box>
            <motion.div>
              <Button
                onClick={handleNextPage}
                disabled={!hasNextPage || loading}
                variant="contained"
                sx={{
                  borderRadius: 3,
                  fontWeight: 700,
                  px: 4,
                  background: "linear-gradient(135deg, rgb(20, 28, 38) 0%, rgb(18, 32, 48) 100%)",
                  boxShadow: "0 8px 25px rgba(30, 30, 60, 0.4)",
                  "&:disabled": {
                    background: "linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)",
                    boxShadow: "none",
                  },
                  "&:hover": {
                    background: "linear-gradient(135deg, rgb(20, 28, 38) 0%, rgb(18, 32, 48) 100%)",
                    boxShadow: "0 8px 25px rgba(30, 30, 60, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Next →
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </motion.div>





      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, requestId: null })}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
            color: "white",
            borderRadius: "16px 16px 0 0",
          }}
        >
          🗑️ Delete Request
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Typography variant="h6" fontWeight={600}>
            Are you sure you want to delete this request?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, requestId: null })}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Cancel
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={handleDelete}
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                background: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
                boxShadow: "0 8px 25px rgba(211, 47, 47, 0.3)",
              }}
            >
              🗑️ Delete
            </Button>
          </motion.div>
        </DialogActions>
      </Dialog>

      <FilterDialog
        open={filterOpen}
        onClose={handleFilterClose}
        startDate={filterStartDate}
        setStartDate={setFilterStartDate}
        endDate={filterEndDate}
        setEndDate={setFilterEndDate}
        departmentName={filterDepartment}
        setDepartmentName={setFilterDepartment}
        email={filterEmail}
        setEmail={setFilterEmail}
        sortOrder={filterSortOrder}
        setSortOrder={setFilterSortOrder}
        applyFilters={applyFilters}
        onClear={clearFilters}
      />

      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  )
}

export default AllRequestList
